import React, { useState, useEffect, useCallback } from 'react';
import Header from './Header';
import TerminationTable from './TerminationTable';
import EditTimeModal from './EditTimeModal';
import SkeletonTable from './SkeletonTable';
import EmptyState from './EmptyState';
import ConfirmDialog from './ConfirmDialog';
import { useToast } from './ToastContext';
import { api } from '../utils/api';
import '../styles/Dashboard.css';

function Dashboard() {
  const [terminations, setTerminations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLitHold, setFilterLitHold] = useState('all');
  const [sortField, setSortField] = useState('processing_time');
  const [sortDir, setSortDir] = useState('asc');
  const [confirmAction, setConfirmAction] = useState(null);

  const toast = useToast();

  const fetchTerminations = useCallback(async () => {
    try {
      setError(null);
      const data = await api.getTerminations();
      setTerminations(data.terminations || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTerminations();
    const interval = setInterval(fetchTerminations, 30000);
    return () => clearInterval(interval);
  }, [fetchTerminations]);

  const handleRemove = (ticketKey) => {
    setConfirmAction({
      title: 'Remove from Queue',
      message: `Are you sure you want to remove ${ticketKey} from the termination queue? This action cannot be undone.`,
      confirmLabel: 'Remove',
      ticketKey,
    });
  };

  const confirmRemove = async () => {
    const { ticketKey } = confirmAction;
    setConfirmAction(null);
    setActionLoading(ticketKey);
    try {
      await api.deleteTermination(ticketKey);
      setTerminations((prev) => prev.filter((t) => t.ticket_key !== ticketKey));
      toast(`${ticketKey} removed from queue`, 'success');
    } catch (err) {
      toast(`Failed to remove: ${err.message}`, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleEdit = (termination) => {
    setEditingItem(termination);
  };

  const handleSaveTime = async (ticketKey, newTime) => {
    setActionLoading(ticketKey);
    try {
      const result = await api.updateTermination(ticketKey, { processing_time: newTime });
      setTerminations((prev) =>
        prev.map((t) => (t.ticket_key === ticketKey ? result.termination : t))
      );
      setEditingItem(null);
      toast(`Processing time updated for ${ticketKey}`, 'success');
    } catch (err) {
      toast(`Failed to update: ${err.message}`, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterLitHold('all');
  };

  const hasFilter = searchTerm !== '' || filterLitHold !== 'all';

  const filteredTerminations = terminations
    .filter((t) => {
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        return (
          t.ticket_key.toLowerCase().includes(q) ||
          t.terminated_user.toLowerCase().includes(q) ||
          (t.support_user && t.support_user.toLowerCase().includes(q))
        );
      }
      return true;
    })
    .filter((t) => {
      if (filterLitHold === 'yes') return t.lit_hold;
      if (filterLitHold === 'no') return !t.lit_hold;
      return true;
    })
    .sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortField === 'processing_time') {
        return dir * (new Date(a.processing_time) - new Date(b.processing_time));
      }
      if (sortField === 'lit_hold') {
        return dir * ((a.lit_hold ? 1 : 0) - (b.lit_hold ? 1 : 0));
      }
      const aVal = a[sortField] || '';
      const bVal = b[sortField] || '';
      return dir * aVal.localeCompare(bVal);
    });

  return (
    <div className="dashboard">
      <Header />
      <main className="main-content" role="main">
        <div className="dashboard-toolbar">
          <div className="toolbar-left">
            <h2 className="page-title">Termination Queue</h2>
            <span className="queue-badge" role="status" aria-label={`${filteredTerminations.length} items in queue`}>
              {filteredTerminations.length}
            </span>
          </div>
          <div className="toolbar-right">
            <div className="search-field">
              <svg className="search-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="6.5" cy="6.5" r="5.5" stroke="currentColor" strokeWidth="1.5" />
                <path d="M10.5 10.5L14.5 14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <input
                type="search"
                placeholder="Search tickets, emails..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
                aria-label="Search terminations"
              />
            </div>
            <select
              value={filterLitHold}
              onChange={(e) => setFilterLitHold(e.target.value)}
              className="filter-select"
              aria-label="Filter by litigation hold"
            >
              <option value="all">All Items</option>
              <option value="yes">Lit Hold Only</option>
              <option value="no">No Lit Hold</option>
            </select>
            <button
              onClick={fetchTerminations}
              className="refresh-button"
              disabled={loading}
              aria-label="Refresh queue"
            >
              <svg
                className={`refresh-icon ${loading ? 'spinning' : ''}`}
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M13.5 8a5.5 5.5 0 01-10.58 2.12M2.5 8a5.5 5.5 0 0110.58-2.12"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <path d="M13.5 3v3.5H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M2.5 13v-3.5H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message" role="alert">
            {error}
          </div>
        )}

        {loading && terminations.length === 0 ? (
          <SkeletonTable rows={5} />
        ) : filteredTerminations.length === 0 ? (
          <EmptyState hasFilter={hasFilter} onClearFilter={clearFilters} />
        ) : (
          <TerminationTable
            terminations={filteredTerminations}
            onEdit={handleEdit}
            onRemove={handleRemove}
            actionLoading={actionLoading}
            sortField={sortField}
            sortDir={sortDir}
            onSort={handleSort}
          />
        )}
      </main>

      {editingItem && (
        <EditTimeModal
          termination={editingItem}
          onSave={handleSaveTime}
          onClose={() => setEditingItem(null)}
          loading={actionLoading === editingItem.ticket_key}
        />
      )}

      {confirmAction && (
        <ConfirmDialog
          title={confirmAction.title}
          message={confirmAction.message}
          confirmLabel={confirmAction.confirmLabel}
          variant="danger"
          onConfirm={confirmRemove}
          onCancel={() => setConfirmAction(null)}
          loading={false}
        />
      )}
    </div>
  );
}

export default Dashboard;
